import LinkSolstice from "@/components/shared/LinkSolstice/LinkSolstice";
import { MaterialIcon } from "@/components/shared/MaterialIcon/MaterialIcon";
import { splitStegaLabel } from "@/sanity/stega";
import clsx from "clsx";
import { MaterialSymbol } from "material-symbols";
import { LinkProps } from "next/link";
import { ComponentPropsWithoutRef } from "react";
import styles from "./Button.module.scss";

export interface ButtonProps {
	label?: React.ReactNode;
	showArrow?: boolean;
	className?: string;
	innerClassName?: string;
	theme?: "primary" | "secondary" | "tertiary" | "compact";
	icon?: MaterialSymbol;
}

export const Button = ({
	label,
	showArrow,
	className,
	innerClassName,
	theme = "primary",
	icon,
	...buttonProps
}: ButtonProps & ComponentPropsWithoutRef<"button">) => {
	const stega = resolveStegaLabel(label);

	return (
		<button
			className={clsx(styles["button-wrapper"], className)}
			data-sanity={stega.dataSanity}
			{...buttonProps}
		>
			<ButtonInternal
				{...{
					label: stega.label,
					showArrow,
					innerClassName,
					theme,
					icon,
				}}
			/>
		</button>
	);
};

export const ButtonLink = ({
	label,
	showArrow,
	className,
	innerClassName,
	theme = "primary",
	icon,
	...linkProps
}: ButtonProps & LinkProps & ComponentPropsWithoutRef<"a">) => {
	const stega = resolveStegaLabel(label);

	return (
		<LinkSolstice
			className={clsx(styles["button-wrapper"], className)}
			data-sanity={stega.dataSanity}
			{...linkProps}
		>
			<ButtonInternal
				{...{
					label: stega.label,
					showArrow,
					innerClassName,
					theme,
					icon,
				}}
			/>
		</LinkSolstice>
	);
};

/**
 * Moves a CMS label's stega source map off the text and onto a data-sanity
 * attribute, so the button stays clickable in the Presentation tool without the
 * invisible characters inflating its width. Non-string labels pass through.
 */
const resolveStegaLabel = (label: React.ReactNode) => {
	if (typeof label !== "string") {
		return { label, dataSanity: undefined };
	}

	const { text, dataSanity } = splitStegaLabel(label);

	return { label: text, dataSanity };
};

const ButtonInternal = ({ label, showArrow, innerClassName, theme, icon }: ButtonProps) => {
	return (
		<span className={clsx(styles["button"], styles[`button--${theme}`], innerClassName)}>
			{label}
			{showArrow && (
				<div className={styles["button__arrow-wrapper"]}>
					<MaterialIcon
						name="arrow_right_alt"
						className={styles["button__arrow"]}
					/>
				</div>
			)}

			{icon && (
				<MaterialIcon
					name={icon}
					className={styles["button__icon"]}
				/>
			)}
		</span>
	);
};
